import React, { FC, useContext, useMemo } from 'react';

// OL
import OlLayerGroup from 'ol/layer/Group';
import OlBaseLayer from 'ol/layer/Base';
import OlCollection from 'ol/Collection';
import OlTileLayer from 'ol/layer/Tile';
import OlImageLayer from 'ol/layer/Image';

// Custom Components
import TileLayer from '@/components/Map/Layers/TileLayer';
import ImageLayer from '@/components/Map/Layers/ImageLayer';
import MapContext, { MapContextType } from '@/context/MapContext/MapContext';
import { flattenLayers } from '@/lib/olHelpers';

// Types
import { ILayerSwiperState } from '@/@types/components/Map/Controls/Custom/LayerTree';

const GSURL = process.env.REACT_APP_GEOSERVERPATH;

type GeoLayerCombinedProps = {
  groupId: string;
  layersCollection: OlCollection<OlBaseLayer>;
  visible?: boolean;
  swipeValue?: number | null
  swipeState?: ILayerSwiperState
};

const GeoLayerCombined: FC<GeoLayerCombinedProps> = (props) => {
  const mapContext = useContext<MapContextType | null>(MapContext);
  const { groupId, visible, layersCollection, swipeValue, swipeState } = props;

  const checkIfVisible = (l: OlBaseLayer) => {
    const layerId = l.get('id');
    const defaultVisible = l.get('visible');

    // 1. check if visible is explicitly set
    if (visible === true || visible === false) {
      return visible;
    }
    
    // 2. check local storage
    const contextVisibility = mapContext?.layerVisibility;
    if (contextVisibility) {
      const hasKey = contextVisibility[layerId] !== undefined;
      if (hasKey) {
        const contextVisible = contextVisibility[layerId];
        if (contextVisible === false || contextVisible === true) {
          return contextVisible;
        }
      }
    }

    // 3. check DB default
    if (defaultVisible === false || defaultVisible === true) {
      return defaultVisible;
    }

    // 4. Application default
    return true;
  }


  const allLayers = flattenLayers(layersCollection.getArray()).filter(
    (x) => !(x instanceof OlLayerGroup)
  );
  // console.log(allLayers);

  const visibleLayers = allLayers.filter(l => checkIfVisible(l) === true);

  const GSlayerNames = visibleLayers.map((x) => x.get('layer'));
  const GSlayerStyles = visibleLayers.map((x) => x.get('styles'));

  const hasVisibleLayers = visibleLayers.length > 0;

  const firstLayer = hasVisibleLayers ? visibleLayers[0] : null;

  const format = hasVisibleLayers ? visibleLayers[0].get('format') : 'image/png8';

  const isTileLayer = hasVisibleLayers ? visibleLayers[0] instanceof OlTileLayer : true;
  const isImageLayer = hasVisibleLayers ? visibleLayers[0] instanceof OlImageLayer : false;

  const tiled = visibleLayers.length > 0 ? visibleLayers[0].get('tiled') : true;

  // this will work poorly with grouped call layers due to the nature of having one image for multiple layers
  const firstLayerSwipeState = useMemo(() => swipeState && visibleLayers[0] ? swipeState[visibleLayers[0].get('id')] : undefined, [swipeState, visibleLayers])

  // console.log('render GSCombined', id, GSlayerNames, visible);

  if (isTileLayer) {
    return (
      <TileLayer
        id={groupId}
        title={groupId}
        visible={hasVisibleLayers}
        swipeValue={swipeValue}
        swipeState={firstLayerSwipeState}
        wms={{
          url: GSURL,
          params: {
            LAYERS: GSlayerNames.join(','),
            FORMAT: format,
            TILED: tiled,
          },
        }}
      />
    );
  }
  
  if (isImageLayer) {
    return (
      <ImageLayer
        id={groupId}
        visible={hasVisibleLayers}
        zIndex={firstLayer?.get('z_index')}
        extent={firstLayer?.get('extent')}
        swipeValue={swipeValue}
        swipeState={firstLayerSwipeState}
        wms={{
          url: GSURL,
          serverType: 'geoserver',
          ratio: 1.2,
          params: {
            LAYERS: GSlayerNames.join(','),
            FORMAT: format,
            STYLES: GSlayerStyles.join(','),
            authkey: localStorage.getItem('auth_token') || undefined,
            viewparams: typeof firstLayer?.get('params') === 'object'
              ? Object.entries(firstLayer?.get('params'))
                .map(([key, val]) => `${key}:${val}`)
                .join(';')
              : undefined
          },
        }}
      />
    );
  }

  return null;
}

export default GeoLayerCombined;
